<template>
	<main>
		<HeaderTab 
			:title="$t('horse.mouvements')"
			:icon="['fal', 'route']"
		/>
		
		<div id="content">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12">
						<div class="mb-3">
							<DateRangeV2
								:start.sync="start_date"
								:end.sync="end_date"
								@submit="load_mouvements"
								dateSelect
								:periodToShow="periodToShow"
								persistId="mouvements_ca"
							/>
						</div>
						
						<LoadingSpinner v-if="loading" />
						
						<template v-else>
							<EmptyState v-if="mouvements.length === 0" :label="$t('mouvement.no_mouvements')" />
							
							<template v-else>
								<!-- Version mobile : cards -->
								<div class="d-md-none">
									<div 
										v-for="(mouvement, index) in mouvements" 
										:key="'card-' + (mouvement.mouvement_id || index)"
										class="card mb-3"
									>
										<div class="card-body">
											<div class="d-flex justify-content-between align-items-start mb-2">
												<h6 class="card-title mb-0">
													<router-link :to="{ name: 'horseFiche', params: { horse_id: mouvement.horse_id }}">
														{{ mouvement.horse_mouvement }}
													</router-link>
												</h6>
												<span class="badge" :class="getMouvementBadgeClass(mouvement.mouvement_type)">
													<font-awesome-icon :icon="getMouvementIcon(mouvement.mouvement_type)" />
													{{ formatDate(mouvement.mouvement_date) }}
												</span>
											</div>
											<p class="card-text mb-1" v-if="mouvement.mouvement_label">
												<strong>{{ mouvement.mouvement_label }}</strong>
											</p>
											<p class="card-text text-muted mb-1" v-if="mouvement.mouvement_adresse || mouvement.mouvement_cp || mouvement.mouvement_ville">
												<font-awesome-icon :icon="['far', 'map-marker-alt']" class="mr-1" />
												{{ [mouvement.mouvement_adresse, mouvement.mouvement_cp, mouvement.mouvement_ville].filter(Boolean).join(', ') }}
											</p>
											<p class="card-text text-muted mb-0" v-if="mouvement.mouvement_raison">
												<small>{{ mouvement.mouvement_raison }}</small>
											</p>
										</div>
									</div>
								</div>
								
								<!-- Version desktop : tableau -->
								<table class="table table-hover d-none d-md-table">
									<thead>
										<tr>
											<th>{{ $t('mouvement.mouvement_horse') }}</th>
											<th>{{ $t('mouvement.mouvement_date') }}</th>
											<th>{{ $t('mouvement.mouvement_label') }}</th>
											<th>{{ $t('mouvement.mouvement_adresse') }}</th>
											<th>{{ $t('mouvement.mouvement_raison') }}</th>
										</tr>
									</thead>
									<tbody>
											<tr v-for="(mouvement, index) in mouvements" :key="mouvement.mouvement_id || index">
											<td>
												<router-link :to="{ name: 'horseFiche', params: { horse_id: mouvement.horse_id }}">
													{{ mouvement.horse_mouvement }}
												</router-link>
											</td>
											<td>
												<font-awesome-icon 
													:style="{ color: getMouvementColor(mouvement.mouvement_type) }" 
													:icon="getMouvementIcon(mouvement.mouvement_type)" 
												/>
												{{ formatDate(mouvement.mouvement_date) }}
											</td>
											<td>{{ mouvement.mouvement_label }}</td>
											<td>{{ formatAdresse(mouvement) }}</td>
											<td>{{ mouvement.mouvement_raison }}</td>
										</tr>
									</tbody>
								</table>
							</template>
						</template>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>

<script type="text/javascript">
import Horse from '@/mixins/Horse.js'

export default {
	name: 'Mouvements',
	mixins: [Horse],
	data() {
		return {
			loading: false,
			mouvements: [],
			start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)),
			end_date: new Date(),
			periodToShow: ['personalise', 'mois', 'mois_dernier', 'annee_en_cours']
		}
	},
	
	created() {
		this.load_mouvements()
	},
	
	methods: {
		async load_mouvements() {
			this.loading = true
			const data = await this.getMouvements(this.start_date, this.end_date)
			// Tri par date décroissante (plus récent en premier)
			this.mouvements = data.sort((a, b) => new Date(b.mouvement_date) - new Date(a.mouvement_date))
			this.loading = false
		},

		/**
		 * Retourne la couleur CSS pour le type de mouvement.
		 * @param {number} type - Type de mouvement (1=entrée, 5=interne, autre=sortie)
		 * @returns {string} Couleur CSS
		 */
		getMouvementColor(type) {
			if (type === 1) return 'green'
			if (type === 5) return 'blue'
			return 'red'
		},

		/**
		 * Retourne la classe Bootstrap badge pour le type de mouvement.
		 * @param {number} type - Type de mouvement
		 * @returns {string} Classe CSS du badge
		 */
		getMouvementBadgeClass(type) {
			if (type === 1) return 'badge-success'
			if (type === 5) return 'badge-primary'
			return 'badge-danger'
		},

		/**
		 * Retourne l'icône FontAwesome pour le type de mouvement.
		 * @param {number} type - Type de mouvement
		 * @returns {string[]} Tableau [pack, nom] de l'icône
		 */
		getMouvementIcon(type) {
			if (type === 1) return ['fal', 'sign-in-alt']
			if (type === 5) return ['fal', 'farm']
			return ['fal', 'sign-out-alt']
		},

		/**
		 * Formate l'adresse complète d'un mouvement.
		 * @param {Object} mouvement - Objet mouvement
		 * @returns {string} Adresse formatée ou '-'
		 */
		formatAdresse(mouvement) {
			const parts = [
				mouvement.mouvement_adresse,
				mouvement.mouvement_cp,
				mouvement.mouvement_ville,
				mouvement.mouvement_pays
			].filter(Boolean)
			return parts.join(', ') || '-'
		}
	},
	
	components: {
		LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		DateRangeV2: () => import('GroomyRoot/components/Inputs/DateRangeV2'),
		HeaderTab: () => import('@/components/HeaderTab'),
		EmptyState: () => import('@/components/EmptyState')
	}
}
</script>